import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, ShoppingCart, Trash2, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'groceries', label: 'Groceries', color: '#16A34A' },
  { id: 'health', label: 'Health', color: '#DC2626' },
  { id: 'cosmetics', label: 'Cosmetics', color: '#EC4899' },
  { id: 'kids', label: 'Kids', color: '#8B5CF6' },
  { id: 'business', label: 'Business Items', color: '#D4AF37' },
  { id: 'main', label: 'Main / Household', color: '#3b82f6' },
  { id: 'other', label: 'Other', color: '#8B7355' },
];

const catMap = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

const ShoppingList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'groceries', price: '', quantity: 1 });
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try { const res = await axios.get(`${API}/api/shopping`, { withCredentials: true }); setItems(res.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const res = await axios.post(`${API}/api/shopping`, { ...form, price: parseFloat(form.price) || 0 }, { withCredentials: true });
      setItems(prev => [res.data, ...prev]);
      setForm({ name: '', category: form.category, price: '', quantity: 1 });
    } catch (err) { console.error(err); }
  };

  const toggleCheck = async (item) => {
    try {
      await axios.patch(`${API}/api/shopping/${item.id}`, { checked: !item.checked }, { withCredentials: true });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, checked: !i.checked } : i));
    } catch (err) { console.error(err); }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/api/shopping/${id}`, { withCredentials: true });
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err) { console.error(err); }
  };

  const clearChecked = async () => {
    try {
      await axios.delete(`${API}/api/shopping/clear/checked`, { withCredentials: true });
      setItems(prev => prev.filter(i => !i.checked));
    } catch (err) { console.error(err); }
  };

  const filtered = useMemo(() => {
    if (activeTab === 'all') return items;
    return items.filter(i => i.category === activeTab);
  }, [items, activeTab]);

  const unchecked = filtered.filter(i => !i.checked);
  const checked = filtered.filter(i => i.checked);

  const totalEstimate = useMemo(() => items.filter(i => !i.checked).reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0), [items]);
  const checkedTotal = useMemo(() => items.filter(i => i.checked).reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0), [items]);

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }} /></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="shopping-list-page">
      <header className="bg-white px-6 lg:px-10 py-5" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-xs mb-3 hover:opacity-70" style={{ color: '#8B7355' }} data-testid="back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-5 h-5" style={{ color: '#D4AF37' }} />
              <h1 className="text-xl font-light" style={{ color: '#2C1A0E' }}>Shopping List</h1>
            </div>
            <div className="text-right">
              <p className="text-lg font-light" style={{ color: '#2C1A0E' }}>£{totalEstimate.toFixed(2)}</p>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8B7355' }}>Estimated total</p>
            </div>
          </div>

          {/* Category tabs */}
          <div className="flex gap-1.5 mt-4 overflow-x-auto pb-1">
            <button onClick={() => setActiveTab('all')} className="px-3 py-1.5 text-xs font-medium rounded-sm border whitespace-nowrap transition-all"
              style={{ borderColor: activeTab === 'all' ? '#D4AF37' : '#E5E1DB', backgroundColor: activeTab === 'all' ? '#D4AF3715' : '#FFF', color: activeTab === 'all' ? '#B8860B' : '#8B7355' }}
              data-testid="tab-all">All ({items.length})</button>
            {CATEGORIES.map(cat => {
              const count = items.filter(i => i.category === cat.id).length;
              if (count === 0 && activeTab !== cat.id) return null;
              return (
                <button key={cat.id} onClick={() => setActiveTab(cat.id)} className="px-3 py-1.5 text-xs font-medium rounded-sm border whitespace-nowrap transition-all"
                  style={{ borderColor: activeTab === cat.id ? cat.color : '#E5E1DB', backgroundColor: activeTab === cat.id ? cat.color + '15' : '#FFF', color: activeTab === cat.id ? cat.color : '#8B7355' }}
                  data-testid={`tab-${cat.id}`}>{cat.label} ({count})</button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 lg:px-10 py-5">
        {/* Quick Add */}
        <form onSubmit={addItem} className="bg-white border rounded-sm p-3 mb-4 flex flex-wrap gap-2" style={{ borderColor: '#E5E1DB' }} data-testid="add-item-form">
          <input type="text" placeholder="Add item..." value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="flex-1 min-w-[150px] px-3 py-2 text-sm border rounded-sm" style={{ borderColor: '#E5E1DB' }} autoFocus data-testid="input-item-name" />
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="px-2 py-2 text-xs border rounded-sm" style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-item-cat">
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <input type="number" step="0.01" placeholder="£" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}
            className="w-16 px-2 py-2 text-xs border rounded-sm font-mono" style={{ borderColor: '#E5E1DB' }} data-testid="input-item-price" />
          <button type="submit" disabled={!form.name.trim()} className="px-3 py-2 rounded-sm disabled:opacity-30" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="add-item-btn">
            <Plus className="w-4 h-4" />
          </button>
        </form>

        {/* Unchecked items */}
        {unchecked.length > 0 && (
          <div className="bg-white border rounded-sm mb-4" style={{ borderColor: '#E5E1DB' }}>
            {unchecked.map(item => {
              const cat = catMap[item.category] || catMap.other;
              return (
                <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 group" style={{ borderBottom: '1px solid #F5F1EB' }} data-testid={`item-${item.id}`}>
                  <button onClick={() => toggleCheck(item)} className="w-5 h-5 rounded-sm border-2 flex-shrink-0 flex items-center justify-center hover:bg-amber-50 transition-colors" style={{ borderColor: '#D4D0C8' }} data-testid={`check-${item.id}`} />
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm" style={{ color: '#2C1A0E' }}>{item.name}</span>
                    {item.quantity > 1 && <span className="text-xs ml-1" style={{ color: '#8B7355' }}>x{item.quantity}</span>}
                  </div>
                  {item.price > 0 && <span className="text-xs font-mono flex-shrink-0" style={{ color: '#2C1A0E' }}>£{(item.price * (item.quantity || 1)).toFixed(2)}</span>}
                  <button onClick={() => deleteItem(item.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1" data-testid={`delete-${item.id}`}>
                    <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Checked items */}
        {checked.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#8B7355' }}>Done ({checked.length}){checkedTotal > 0 ? ` · £${checkedTotal.toFixed(2)} spent` : ''}</span>
              <button onClick={clearChecked} className="text-[10px] px-2 py-1 border rounded-sm" style={{ borderColor: '#E5E1DB', color: '#8B7355' }} data-testid="clear-checked">Clear all</button>
            </div>
            <div className="bg-white border rounded-sm" style={{ borderColor: '#E5E1DB' }}>
              {checked.map(item => {
                const cat = catMap[item.category] || catMap.other;
                return (
                  <div key={item.id} className="flex items-center gap-3 px-4 py-2 group" style={{ borderBottom: '1px solid #F5F1EB' }} data-testid={`item-${item.id}`}>
                    <button onClick={() => toggleCheck(item)} className="w-5 h-5 rounded-sm flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: '#16A34A' }} data-testid={`uncheck-${item.id}`}>
                      <Check className="w-3 h-3 text-white" />
                    </button>
                    <div className="w-2 h-2 rounded-full flex-shrink-0 opacity-40" style={{ backgroundColor: cat.color }} />
                    <span className="text-sm line-through flex-1" style={{ color: '#8B7355' }}>{item.name}</span>
                    {item.price > 0 && <span className="text-xs font-mono line-through" style={{ color: '#8B7355' }}>£{(item.price * (item.quantity || 1)).toFixed(2)}</span>}
                    <button onClick={() => deleteItem(item.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1">
                      <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {items.length === 0 && (
          <div className="py-16 text-center">
            <ShoppingCart className="w-10 h-10 mx-auto mb-3" style={{ color: '#E5E1DB' }} />
            <p className="text-sm" style={{ color: '#8B7355' }}>Your shopping list is empty</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ShoppingList;
